import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { ESubscriptionType } from "common/enums/Subscription/ESubscriptionType";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import SubscriptionAdminCreateRequestResource from "common/resources/Subscription/SubscriptionAdminCreateRequestResource";
import SubscriptionCorporationAdminCreateRequestResource from "common/resources/Subscription/SubscriptionCorporationAdminCreateRequestResource";
import Button, { EButtonVariant } from "components/elements/Button";
import Typography, { ITypo } from "components/elements/Typography";
import I18n from "components/materials/I18n";
import ModuleConfig from "configs/ModuleConfig";
import useOpenable from "hooks/useOpenable";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SubscriptionService from "services/SubscriptionService";
import { container } from "tsyringe";
import NumberUtils from "utils/NumberUtils";
import classes from "./classes.module.scss";
import ProductSelectedTable from "./ProductSelectedTable";
import ProductSelectorTable, { IProductSelected } from "./ProductSelectorTable";
import ProductSubscriptionCreateResource from "common/resources/ProductSubscribed/ProductSubscriptionCreateResource";
import ProductSubscriptionCorporationCreateResource from "common/resources/ProductSubscribed/ProductSubscriptionCorporationCreateResource";
import { manualSubscriptionValidationMessage } from "../validationMessage";
import StatusSelectionModal from "../StatusSelectionModal";
import Modal from "components/elements/Modal";
import ManualSubscriptionProductsRequestResource, { ManualSubscriptionProductAvailability } from "common/resources/Subscription/ManualSubscriptionProductsRequestResource";

const subscriptionService = container.resolve(SubscriptionService);
const modules = container.resolve(ModuleConfig).get().modules;

type IProps = {
	clientSheetId: string;
	subscriberType: "physical" | "legal";
	cgpId: string;
	officeId: string;
	isCoSubscription?: boolean;
};

function isProductSubscriptionCorporationCreateResource(
	dto: ProductSubscriptionCreateResource | ProductSubscriptionCorporationCreateResource,
): dto is ProductSubscriptionCorporationCreateResource {
	return "corporationSubscriberId" in dto;
}

function isProductSubscriptionCreateResource(dto: ProductSubscriptionCreateResource | ProductSubscriptionCorporationCreateResource): dto is ProductSubscriptionCreateResource {
	return "subscriberId" in dto;
}

export default function SelectProducts(props: IProps) {
	const [productsInSubscription, setProductsInSubscription] = useState<IProductSelected[]>([]);
	const navigate = useNavigate();
	const statusModal = useOpenable();
	const submitting = useRef(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [availability, setAvailability] = useState<ManualSubscriptionProductAvailability | null>(null);
	const [pendingStatus, setPendingStatus] = useState<{ status: ESubStep; validationDate?: Date } | null>(null);
	const [availabilityError, setAvailabilityError] = useState<string | null>(null);

	const onCreateSubscription = useCallback(() => {
		// Ouvrir le modal pour choisir le statut avant de créer
		statusModal.open();
	}, [statusModal]);

	const onCancel = useCallback(() => navigate(modules.pages.Subscriptions.props.pages.InProgress.props.path), [navigate]);

	const handleStatusUpdate = useCallback(
		async (status: ESubStep, validationDate?: Date, authorizeProducts = false) => {
			if (submitting.current) return;
			submitting.current = true;
			setIsSubmitting(true);
			setAvailabilityError(null);
			let productsAuthorized = false;
			try {
				const request = ManualSubscriptionProductsRequestResource.hydrate<ManualSubscriptionProductsRequestResource>({
					officeId: props.officeId,
					creatorId: props.cgpId,
					productSheetIds: productsInSubscription.map((product) => product.dto.productSheetId),
				});
				const checked: ManualSubscriptionProductAvailability = authorizeProducts
					? await subscriptionService.authorizeProducts(request)
					: await subscriptionService.checkProductAvailability(request);
				productsAuthorized = authorizeProducts;
				if (checked.missingProducts.length || checked.unavailableProducts.length) {
					setAvailability(checked);
					setPendingStatus({ status, validationDate });
					return;
				}
				setAvailability(null);
				if (props.subscriberType === "legal") {
					const corporationProducts = productsInSubscription.map((p) => p.dto).filter(isProductSubscriptionCorporationCreateResource);
					const resource = SubscriptionCorporationAdminCreateRequestResource.hydrate<SubscriptionCorporationAdminCreateRequestResource>({
						creatorId: props.cgpId,
						productsSubscribed: corporationProducts,
						subscriptionType: ESubscriptionType.legalPerson,
					});

					if (status === ESubStep.validated && validationDate) {
						await subscriptionService.postCorporationAsAdminValidated(resource, validationDate);
					} else {
						await subscriptionService.postCorporationAsAdmin(resource);
					}
				} else {
					const physicalProducts = productsInSubscription.map((p) => p.dto).filter(isProductSubscriptionCreateResource);
					const subscriptionType = props.isCoSubscription ? ESubscriptionType.coSubscriptionNaturalPerson : ESubscriptionType.naturalPerson;
					const resource = SubscriptionAdminCreateRequestResource.hydrate<SubscriptionAdminCreateRequestResource>({
						creatorId: props.cgpId,
						productsSubscribed: physicalProducts,
						subscriptionType,
					});

					if (status === ESubStep.validated && validationDate) {
						await subscriptionService.postAsAdminValidated(resource, validationDate);
					} else {
						await subscriptionService.postAsAdmin(resource);
					}
				}

				statusModal.close();
				toast.success("Souscription créée");
				navigate(modules.pages.MOSubscriptions.props.path);
			} catch (error) {
				const businessMessage = manualSubscriptionValidationMessage(error);
				const failureMessage = businessMessage ?? "La souscription n'a pas pu être créée. Votre saisie est conservée. Réessayez ou contactez le support.";
				const message = productsAuthorized ? `Les produits ont été associés au cabinet. ${failureMessage}` : failureMessage;
				setAvailabilityError(message);
				toast.error(message);
			} finally {
				submitting.current = false;
				setIsSubmitting(false);
			}
		},
		[productsInSubscription, props.subscriberType, props.cgpId, props.officeId, props.isCoSubscription, statusModal, navigate],
	);

	return (
		<div className={classes["root"]}>
			<Typography className={classes["subtitle"]} typo={ITypo.P_MEDIUM_BOLD}>
				{I18n.asset.pages.subscriptions.select_products.subtitle}
			</Typography>

			<div className={classes["table-container"]}>
				<ProductSelectorTable
					productsInSubscription={productsInSubscription}
					setProductsInSubscription={setProductsInSubscription}
					subscriberId={props.clientSheetId}
					subscriberType={props.subscriberType}
				/>
				<div className={classes["separator"]} />
				<ProductSelectedTable productsInSubscription={productsInSubscription} setProductsInSubscription={setProductsInSubscription} />
			</div>
			<div className={classes["total-container"]}>
				<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["total-label"]}>
					{I18n.asset.pages.subscriptions.select_products.total_amount}
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["total-number"]}>
					{NumberUtils.formatNumberAddingSpaces(productsInSubscription.reduce((acc, product) => acc + product.dto.quantity * product.product.price, 0) ?? 0) + " €"}
				</Typography>
			</div>

			<div className={classes["button-container"]}>
				<Button variant={EButtonVariant.OUTLINED} onClick={onCancel}>
					{I18n.asset.common.cancel}
				</Button>
				<Button onClick={onCreateSubscription} disabled={!productsInSubscription.length}>
					{I18n.asset.pages.subscriptions.select_products.next_page}
					<ArrowLongRightIcon />
				</Button>
			</div>

			<StatusSelectionModal isOpen={statusModal.isOpen && !availability} onClose={statusModal.close} onSave={handleStatusUpdate} isSubmitting={isSubmitting} />
			<Modal isOpen={!!availability} onClose={isSubmitting ? undefined : () => setAvailability(null)}>
				{availability && (
					<div className={classes["availability-modal"]} role="dialog" aria-modal="true" aria-labelledby="product-availability-title">
						<h2 id="product-availability-title">{availability.unavailableProducts.length ? "Produit indisponible" : "Produit non associé au cabinet"}</h2>
						{availability.missingProducts.length > 0 && (
							<>
								<p>
									Les produits suivants ne sont pas associés au cabinet <strong>{availability.officeName}</strong> :
								</p>
								<ul>
									{availability.missingProducts.map((product) => (
										<li key={product.id}>{product.name}</li>
									))}
								</ul>
								{!availability.unavailableProducts.length && (
									<p>
										En confirmant, vous les rendrez disponibles pour les conseillers de ce cabinet, puis la souscription sera créée avec le statut et la date
										choisis.
									</p>
								)}
							</>
						)}
						{availability.unavailableProducts.length > 0 && (
							<>
								<p>Ces produits sont inactifs et ne peuvent pas être ajoutés au cabinet. Retirez-les du panier pour continuer :</p>
								<ul>
									{availability.unavailableProducts.map((product) => (
										<li key={product.id}>{product.name}</li>
									))}
								</ul>
							</>
						)}
						{availabilityError && <p role="alert">{availabilityError}</p>}
						<div className={classes["availability-actions"]}>
							<Button
								variant={EButtonVariant.OUTLINED}
								disabled={isSubmitting}
								onClick={() => {
									setAvailability(null);
									statusModal.close();
								}}>
								Revenir à la saisie
							</Button>
							{!availability.unavailableProducts.length && pendingStatus && (
								<Button disabled={isSubmitting} onClick={() => handleStatusUpdate(pendingStatus.status, pendingStatus.validationDate, true)}>
									{isSubmitting ? "Enregistrement…" : "Ajouter au cabinet et créer la souscription"}
								</Button>
							)}
						</div>
					</div>
				)}
			</Modal>
		</div>
	);
}
